import type { CuratorOfferSubmissionPlaylist } from './curatorOffer';
import { isNewShapeComplete } from './curatorOffer';
import {
  buildSharedOfferFields,
  type LinkedCuratorProfile,
  type ProfilePlaylistSelection,
} from './accountCuratorOffer';

export type OfferTerms = ReturnType<typeof buildSharedOfferFields>;

export interface ProfilePlaylistSelectionState {
  selectedIds: string[];
  includeAllOwnedPlaylists?: boolean;
}

export interface CuratorOfferTemplate {
  id: string;
  label: string;
  terms: OfferTerms;
  profileIds: string[];
  profilePlaylistSelections: Record<string, ProfilePlaylistSelectionState>;
  updatedAt: string;
}

export interface CuratorOfferLibraryState {
  version: 1;
  accountUserId: string;
  offers: CuratorOfferTemplate[];
  activeOfferId: string | null;
}

const STORAGE_PREFIX = 'songplace_curator_offer_library_v1';

const createOfferId = () =>
  `offer_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const getOfferLibraryStorageKey = (accountUserId: string) =>
  `${STORAGE_PREFIX}_${accountUserId}`;

export const loadOfferLibrary = (accountUserId: string): CuratorOfferLibraryState | null => {
  try {
    const raw = localStorage.getItem(getOfferLibraryStorageKey(accountUserId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CuratorOfferLibraryState;
    if (parsed?.version !== 1 || parsed.accountUserId !== accountUserId) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveOfferLibrary = (state: CuratorOfferLibraryState) => {
  localStorage.setItem(getOfferLibraryStorageKey(state.accountUserId), JSON.stringify(state));
};

const offerTermsSignature = (terms: OfferTerms) =>
  JSON.stringify({
    dailyStreamsMin: terms.dailyStreamsMin,
    dailyStreamsMax: terms.dailyStreamsMax,
    defaultRatePer1k: terms.defaultRatePer1k,
    notes: terms.notes,
    genres: [...(terms.genres || [])].sort(),
    interestedInFeedback: terms.interestedInFeedback,
    audienceCountryCodes: [...(terms.audienceCountryCodes || [])].sort(),
    audienceCountriesUnknown: terms.audienceCountriesUnknown,
  });

export const areOfferTermsEqual = (left: OfferTerms, right: OfferTerms) =>
  offerTermsSignature(left) === offerTermsSignature(right);

export const arePlaylistIdsEqual = (left: string[], right: string[]) =>
  JSON.stringify([...left].sort()) === JSON.stringify([...right].sort());

export const isQuickEditModalDirty = (
  initial: {
    terms: OfferTerms;
    profileId: string;
    selectedPlaylistIds: string[];
  },
  current: {
    terms: OfferTerms;
    profileId: string;
    selectedPlaylistIds: string[];
  }
) =>
  !areOfferTermsEqual(initial.terms, current.terms) ||
  !arePlaylistIdsEqual(initial.selectedPlaylistIds, current.selectedPlaylistIds);

export const buildOfferEditorSnapshot = ({
  label,
  terms,
  profileIds,
  profilePlaylistSelections,
}: {
  label: string;
  terms: OfferTerms;
  profileIds: string[];
  profilePlaylistSelections: Record<string, ProfilePlaylistSelectionState>;
}) =>
  JSON.stringify({
    label: label.trim(),
    terms: offerTermsSignature(terms),
    profileIds: [...profileIds].sort(),
    profilePlaylistSelections: Object.fromEntries(
      [...profileIds].sort().map(profileId => {
        const selection = profilePlaylistSelections[profileId] || { selectedIds: [] };
        return [
          profileId,
          {
            selectedIds: [...(selection.selectedIds || [])].sort(),
            includeAllOwnedPlaylists: selection.includeAllOwnedPlaylists === true,
          },
        ];
      })
    ),
  });

export const buildLiveOfferPlaylistSelections = (
  offer: Pick<CuratorOfferTemplate, 'profileIds'>,
  profilePlaylists: Record<string, { selectedIds?: string[] }>,
  playlistOptions: Record<string, CuratorOfferSubmissionPlaylist[]>
): Record<string, ProfilePlaylistSelectionState> =>
  Object.fromEntries(
    offer.profileIds.map(profileId => [
      profileId,
      {
        selectedIds: profilePlaylists[profileId]?.selectedIds || [],
        includeAllOwnedPlaylists:
          (playlistOptions[profileId]?.length || 0) > 0 &&
          (profilePlaylists[profileId]?.selectedIds.length || 0) >=
            (playlistOptions[profileId]?.length || 0),
      },
    ])
  );

export const isOfferEditorDirty = (
  savedOffer: CuratorOfferTemplate | null | undefined,
  draft: {
    label: string;
    terms: OfferTerms;
    profileIds: string[];
    profilePlaylistSelections: Record<string, ProfilePlaylistSelectionState>;
  }
) => {
  if (!savedOffer) return true;

  return (
    buildOfferEditorSnapshot(savedOffer) !==
    buildOfferEditorSnapshot({
      label: draft.label,
      terms: draft.terms,
      profileIds: draft.profileIds,
      profilePlaylistSelections: draft.profilePlaylistSelections,
    })
  );
};

export const buildDefaultOfferLabel = (terms: OfferTerms, index: number) => {
  const rate = Number(terms.defaultRatePer1k);
  const genre = terms.genres?.[0];
  if (rate > 0 && genre) {
    return `${genre} · $${rate}/1K`;
  }
  if (rate > 0) {
    return `Offer ${index + 1} · $${rate}/1K`;
  }
  return `Offer ${index + 1}`;
};

const profilePlaylistSelectionFromOffer = (
  profile: LinkedCuratorProfile
): ProfilePlaylistSelectionState => ({
  selectedIds: [
    ...(profile.curatorOffer?.submissionPlaylistIds || []),
    ...(profile.curatorOffer?.submissionPlaylists || []).map(item => item.playlistId),
  ].filter(Boolean),
  includeAllOwnedPlaylists: profile.curatorOffer?.includeAllOwnedPlaylists === true,
});

export const getOpenOfferProfiles = (profiles: LinkedCuratorProfile[]) =>
  profiles.filter(
    profile => profile.acceptCampaigns && isNewShapeComplete(profile.curatorOffer || null)
  );

const getAssignedProfileIds = (library: CuratorOfferLibraryState) =>
  new Set(library.offers.flatMap(offer => offer.profileIds));

const pruneStaleProfilesFromLibrary = (
  library: CuratorOfferLibraryState,
  openProfileIds: Set<string>
): CuratorOfferLibraryState => ({
  ...library,
  offers: library.offers.map(offer => {
    const profileIds = offer.profileIds.filter(id => openProfileIds.has(id));
    const profilePlaylistSelections = Object.fromEntries(
      Object.entries(offer.profilePlaylistSelections).filter(([profileId]) =>
        openProfileIds.has(profileId)
      )
    );
    return {
      ...offer,
      profileIds,
      profilePlaylistSelections,
    };
  }),
});

export const buildOfferLibraryFromServerProfiles = (
  accountUserId: string,
  profiles: LinkedCuratorProfile[]
): CuratorOfferLibraryState => {
  type OfferGroup = {
    offerId: string;
    terms: OfferTerms;
    profileIds: string[];
    selections: Record<string, ProfilePlaylistSelectionState>;
  };

  const groups = new Map<string, OfferGroup>();

  getOpenOfferProfiles(profiles).forEach(profile => {
    const terms = buildSharedOfferFields(profile.curatorOffer);
    const templateId =
      typeof profile.curatorOffer?.offerTemplateId === 'string'
        ? profile.curatorOffer.offerTemplateId.trim()
        : '';
    const groupKey = templateId || `sig:${offerTermsSignature(terms)}`;
    const offerId = templateId || '';

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        offerId: offerId || createOfferId(),
        terms,
        profileIds: [],
        selections: {},
      });
    }

    const group = groups.get(groupKey)!;
    group.profileIds.push(profile.id);
    group.selections[profile.id] = profilePlaylistSelectionFromOffer(profile);
  });

  const offers: CuratorOfferTemplate[] = Array.from(groups.values()).map((group, index) => ({
    id: group.offerId,
    label: buildDefaultOfferLabel(group.terms, index),
    terms: group.terms,
    profileIds: group.profileIds,
    profilePlaylistSelections: group.selections,
    updatedAt: new Date().toISOString(),
  }));

  return {
    version: 1,
    accountUserId,
    offers,
    activeOfferId: offers[0]?.id || null,
  };
};

/** @deprecated Use buildOfferLibraryFromServerProfiles */
export const migrateProfilesToOfferLibrary = buildOfferLibraryFromServerProfiles;

const preserveCustomLabels = (
  serverLibrary: CuratorOfferLibraryState,
  existing: CuratorOfferLibraryState | null
): CuratorOfferLibraryState => {
  if (!existing) {
    return serverLibrary;
  }

  const labelByOfferId = new Map(
    existing.offers.map(offer => [offer.id, offer.label.trim()] as const).filter(([, label]) => label)
  );

  const serverOfferIds = new Set(serverLibrary.offers.map(offer => offer.id));
  const localDraftOffers = existing.offers.filter(
    offer => offer.profileIds.length === 0 && !serverOfferIds.has(offer.id)
  );

  const offers = [
    ...serverLibrary.offers.map((offer, index) => {
      const customLabel = labelByOfferId.get(offer.id);
      if (!customLabel || customLabel === offer.label) {
        return offer;
      }
      if (customLabel.startsWith('Offer ') && !customLabel.includes('·')) {
        return offer;
      }
      return { ...offer, label: customLabel };
    }),
    ...localDraftOffers,
  ];

  const activeOfferId =
    existing.activeOfferId && offers.some(offer => offer.id === existing.activeOfferId)
      ? existing.activeOfferId
      : serverLibrary.activeOfferId;

  return {
    ...serverLibrary,
    offers,
    activeOfferId,
  };
};

export const reconcileOfferLibraryWithServer = (
  accountUserId: string,
  profiles: LinkedCuratorProfile[],
  existing: CuratorOfferLibraryState | null = loadOfferLibrary(accountUserId)
): CuratorOfferLibraryState => {
  const fromServer = buildOfferLibraryFromServerProfiles(accountUserId, profiles);
  const openProfileIds = new Set(getOpenOfferProfiles(profiles).map(profile => profile.id));
  const existingPruned = existing
    ? pruneStaleProfilesFromLibrary(existing, openProfileIds)
    : null;

  if (openProfileIds.size === 0) {
    if (existingPruned) {
      return existingPruned;
    }
    const emptyOffer = createEmptyOfferTemplate(0);
    return {
      version: 1,
      accountUserId,
      offers: [emptyOffer],
      activeOfferId: emptyOffer.id,
    };
  }

  if (!existingPruned || existingPruned.offers.every(offer => offer.profileIds.length === 0)) {
    return preserveCustomLabels(fromServer, existingPruned);
  }

  const localAssigned = getAssignedProfileIds(existingPruned);
  const coversAllServerProfiles = Array.from(openProfileIds).every(profileId =>
    localAssigned.has(profileId)
  );

  if (!coversAllServerProfiles) {
    return preserveCustomLabels(fromServer, existingPruned);
  }

  return existingPruned;
};

export const ensureOfferLibrary = (
  accountUserId: string,
  profiles: LinkedCuratorProfile[]
): CuratorOfferLibraryState => {
  const reconciled = reconcileOfferLibraryWithServer(accountUserId, profiles);
  const withDefaults = withFirstTimeOfferDefaults(reconciled, profiles);
  saveOfferLibrary(withDefaults);
  return withDefaults;
};

export const createEmptyOfferTemplate = (index: number): CuratorOfferTemplate => ({
  id: createOfferId(),
  label: `Offer ${index + 1}`,
  terms: buildSharedOfferFields(null),
  profileIds: [],
  profilePlaylistSelections: {},
  updatedAt: new Date().toISOString(),
});

/** True when the account has a single unsaved draft offer and no saved offers on the server yet. */
export const isFirstTimeOfferDraft = (
  library: CuratorOfferLibraryState,
  profiles: LinkedCuratorProfile[],
  offer: CuratorOfferTemplate
): boolean =>
  library.offers.length === 1 &&
  getOpenOfferProfiles(profiles).length === 0 &&
  offer.profileIds.length === 0;

/** Pre-select every linked profile for a first-time offer draft. Playlists load as all-owned per profile. */
export const applyFirstTimeOfferDefaults = (
  offer: CuratorOfferTemplate,
  profiles: LinkedCuratorProfile[]
): CuratorOfferTemplate => ({
  ...offer,
  profileIds: profiles.map(profile => profile.id),
  profilePlaylistSelections: Object.fromEntries(
    profiles.map(profile => [
      profile.id,
      { selectedIds: [], includeAllOwnedPlaylists: true },
    ])
  ),
  updatedAt: new Date().toISOString(),
});

export const withFirstTimeOfferDefaults = (
  library: CuratorOfferLibraryState,
  profiles: LinkedCuratorProfile[]
): CuratorOfferLibraryState => {
  if (library.offers.length !== 1) return library;

  const offer = library.offers[0];
  if (!isFirstTimeOfferDraft(library, profiles, offer)) return library;

  const nextOffer = applyFirstTimeOfferDefaults(offer, profiles);
  return {
    ...library,
    offers: [nextOffer],
    activeOfferId: nextOffer.id,
  };
};

export const getProfileOfferMap = (offers: CuratorOfferTemplate[]) => {
  const map = new Map<string, CuratorOfferTemplate>();
  offers.forEach(offer => {
    offer.profileIds.forEach(profileId => {
      map.set(profileId, offer);
    });
  });
  return map;
};

export const getUnassignedProfileIds = (
  profiles: LinkedCuratorProfile[],
  offers: CuratorOfferTemplate[]
) => {
  const assigned = new Set(offers.flatMap(offer => offer.profileIds));
  return profiles.filter(profile => !assigned.has(profile.id)).map(profile => profile.id);
};

export const assignProfileToOffer = (
  library: CuratorOfferLibraryState,
  offerId: string,
  profileId: string
): CuratorOfferLibraryState => {
  const offers = library.offers.map(offer => {
    const profileIds = offer.profileIds.filter(id => id !== profileId);
    const { [profileId]: _removed, ...profilePlaylistSelections } = offer.profilePlaylistSelections;

    if (offer.id === offerId) {
      return {
        ...offer,
        profileIds: profileIds.includes(profileId) ? profileIds : [...profileIds, profileId],
        profilePlaylistSelections: {
          ...profilePlaylistSelections,
          [profileId]: offer.profilePlaylistSelections[profileId] || { selectedIds: [] },
        },
        updatedAt: new Date().toISOString(),
      };
    }

    return {
      ...offer,
      profileIds,
      profilePlaylistSelections,
    };
  });

  return { ...library, offers, activeOfferId: offerId };
};

export const removeProfileFromOffer = (
  library: CuratorOfferLibraryState,
  offerId: string,
  profileId: string
): CuratorOfferLibraryState => ({
  ...library,
  offers: library.offers.map(offer => {
    if (offer.id !== offerId) return offer;
    const { [profileId]: _removed, ...profilePlaylistSelections } = offer.profilePlaylistSelections;
    return {
      ...offer,
      profileIds: offer.profileIds.filter(id => id !== profileId),
      profilePlaylistSelections,
      updatedAt: new Date().toISOString(),
    };
  }),
});

export const buildProfileOffersPayload = (
  offer: CuratorOfferTemplate,
  playlistOptions: Record<string, CuratorOfferSubmissionPlaylist[]>
): ProfilePlaylistSelection[] =>
  offer.profileIds.map(profileId => {
    const selection = offer.profilePlaylistSelections[profileId];
    const options = playlistOptions[profileId] || [];
    const selectedIds = selection?.selectedIds || [];
    const submissionPlaylists = options.filter(playlist => selectedIds.includes(playlist.playlistId));

    return {
      profileId,
      submissionPlaylists,
      submissionPlaylistIds: selectedIds,
      includeAllOwnedPlaylists:
        selection?.includeAllOwnedPlaylists === true ||
        (options.length > 0 && selectedIds.length >= options.length),
    };
  });

export const findOfferTemplateForProfile = (
  library: CuratorOfferLibraryState,
  profileId: string
): CuratorOfferTemplate | null => getProfileOfferMap(library.offers).get(profileId) || null;

export const buildCuratorOfferViewForProfile = (
  offer: CuratorOfferTemplate,
  profileId: string
) => {
  const selection = offer.profilePlaylistSelections[profileId];
  return {
    ...offer.terms,
    submissionPlaylistIds: selection?.selectedIds || [],
    submissionPlaylists: [],
    includeAllOwnedPlaylists: selection?.includeAllOwnedPlaylists === true,
    updatedAt: offer.updatedAt,
  };
};

export const applySavedOfferTemplateToLibrary = (
  library: CuratorOfferLibraryState,
  offerDraft: CuratorOfferTemplate
): { nextLibrary: CuratorOfferLibraryState; disabledProfileIds: string[] } => {
  const savedOffer = library.offers.find(offer => offer.id === offerDraft.id);
  const removedFromThisOffer = (savedOffer?.profileIds || []).filter(
    profileId => !offerDraft.profileIds.includes(profileId)
  );

  let nextLibrary = library;
  for (const profileId of removedFromThisOffer) {
    nextLibrary = removeProfileFromOffer(nextLibrary, offerDraft.id, profileId);
  }
  for (const profileId of offerDraft.profileIds) {
    nextLibrary = assignProfileToOffer(nextLibrary, offerDraft.id, profileId);
  }

  nextLibrary = {
    ...nextLibrary,
    activeOfferId: offerDraft.id,
    offers: nextLibrary.offers.map(offer =>
      offer.id === offerDraft.id ? offerDraft : offer
    ),
  };

  const assignedAfterSave = new Set(nextLibrary.offers.flatMap(offer => offer.profileIds));
  const disabledProfileIds = removedFromThisOffer.filter(
    profileId => !assignedAfterSave.has(profileId)
  );

  return { nextLibrary, disabledProfileIds };
};

export const summarizeOffer = (offer: CuratorOfferTemplate) => ({
  profileCount: offer.profileIds.length,
  rate: offer.terms.defaultRatePer1k,
  genres: offer.terms.genres?.slice(0, 2) || [],
});

export const cloneOfferTemplate = (offer: CuratorOfferTemplate): CuratorOfferTemplate => ({
  ...offer,
  terms: { ...offer.terms, genres: [...(offer.terms.genres || [])], audienceCountryCodes: [...(offer.terms.audienceCountryCodes || [])] },
  profileIds: [...offer.profileIds],
  profilePlaylistSelections: Object.fromEntries(
    Object.entries(offer.profilePlaylistSelections).map(([profileId, selection]) => [
      profileId,
      {
        selectedIds: [...(selection.selectedIds || [])],
        includeAllOwnedPlaylists: selection.includeAllOwnedPlaylists,
      },
    ])
  ),
});
